import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Search, Loader2, CheckSquare, Square, RefreshCcw, Users } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui';

const SEARCH_DEBOUNCE_MS = 250;

export interface SchuelerRow {
  ID: number;
  Vorname?: string;
  Name?: string;
  Klasse?: string;
  Email?: string;
}

interface KlassenRow {
  Klasse: string;
  student_count?: number;
}

export function buildDemandsStudentsPayload(
  selectedIds: string[],
  students: SchuelerRow[]
): Array<{ id: string; name: string; email: string | null }> {
  const byId = new Map(students.map((s) => [String(s.ID), s]));
  return selectedIds
    .map((id) => {
      const s = byId.get(id);
      if (!s) return null;
      const vor = String(s.Vorname ?? '').trim();
      const name = String(s.Name ?? '').trim();
      const full = `${vor} ${name}`.trim() || name || String(s.ID);
      return {
        id: String(s.ID),
        name: full,
        email: s.Email && String(s.Email).trim() ? String(s.Email).trim() : null
      };
    })
    .filter(Boolean) as Array<{ id: string; name: string; email: string | null }>;
}

export interface PaymentStudentMultiSelectProps {
  enabled: boolean;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  /** Aktuelle Ergebnisliste (für Payload beim Anlegen der Forderungen). */
  onStudentsChange?: (rows: SchuelerRow[]) => void;
}

/**
 * Kompakte Schülerauswahl (Suche, Klasse, Liste) — gleiche Datenquelle wie Forderungen-anlegen-Modal.
 */
export default function PaymentStudentMultiSelect({
  enabled,
  selectedIds,
  onSelectedIdsChange,
  onStudentsChange
}: PaymentStudentMultiSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedKlasse, setSelectedKlasse] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!enabled) {
      setSearchTerm('');
      setDebouncedSearch('');
      setSelectedKlasse('');
    }
  }, [enabled]);

  const classesQuery = useQuery(
    ['students-classes-picker'],
    async () => {
      const res = await api.get('/students/classes');
      return (res.data?.data ?? []) as KlassenRow[];
    },
    { enabled }
  );

  const studentsQuery = useQuery(
    ['students-picker', debouncedSearch, selectedKlasse],
    async () => {
      const res = await api.get('/students', {
        params: {
          search: debouncedSearch || undefined,
          klasse: selectedKlasse || undefined,
          includePhotos: 'false',
          sortField: 'Name',
          sortDirection: 'asc'
        }
      });
      return (res.data?.data ?? []) as SchuelerRow[];
    },
    { enabled, keepPreviousData: true }
  );

  const students = useMemo(() => studentsQuery.data ?? [], [studentsQuery.data]);
  const loading = studentsQuery.isLoading || studentsQuery.isFetching;

  useEffect(() => {
    onStudentsChange?.(students);
  }, [students, onStudentsChange]);

  useEffect(() => {
    if (!enabled) return;
    if (loading) return;
    if (!students.length) {
      onSelectedIdsChange([]);
      return;
    }
    const next = selectedIds.filter((id) => students.some((s) => String(s.ID) === id));
    if (next.length !== selectedIds.length) onSelectedIdsChange(next);
  }, [enabled, students, selectedIds, loading, onSelectedIdsChange]);

  const toggleSelection = useCallback(
    (studentId: string) => {
      onSelectedIdsChange(
        selectedIds.includes(studentId)
          ? selectedIds.filter((id) => id !== studentId)
          : [...selectedIds, studentId]
      );
    },
    [selectedIds, onSelectedIdsChange]
  );

  const toggleSelectAll = useCallback(() => {
    if (!students.length) return;
    const visible = students.map((s) => String(s.ID));
    const all = visible.every((id) => selectedIds.includes(id));
    onSelectedIdsChange(all ? [] : visible);
  }, [students, selectedIds, onSelectedIdsChange]);

  if (!enabled) return null;

  return (
    <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
      <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">
        Schüler für Forderungen (optional)
      </p>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        Mit Auswahl und Betrag werden beim Anlegen des Vorgangs gleich Forderungen erzeugt. Ohne Auswahl legen Sie nur
        den Vorgang an.
      </p>

      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <div className="md:col-span-2">
          <label htmlFor="pm-create-student-search" className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
            Suche
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              id="pm-create-student-search"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Vorname, Name, Klasse …"
              className="input-field min-h-[2.25rem] py-2 pl-10 text-sm"
              autoComplete="off"
            />
          </div>
        </div>
        <div>
          <label htmlFor="pm-create-student-klasse" className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
            Klasse
          </label>
          <select
            id="pm-create-student-klasse"
            value={selectedKlasse}
            onChange={(e) => setSelectedKlasse(e.target.value)}
            className="input-field min-h-[2.25rem] py-2 text-sm"
          >
            <option value="">Alle Klassen</option>
            {(classesQuery.data ?? []).map((row) => (
              <option key={row.Klasse} value={row.Klasse}>
                Klasse {row.Klasse}
                {row.student_count != null ? ` (${row.student_count})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={toggleSelectAll}>
          {students.length > 0 && students.every((s) => selectedIds.includes(String(s.ID))) ? (
            <>
              <CheckSquare className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Alle abwählen
            </>
          ) : (
            <>
              <Square className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Alle sichtbaren
            </>
          )}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => studentsQuery.refetch()}>
          <RefreshCcw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Aktualisieren
        </Button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          <Users className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden />
          {selectedIds.length} ausgewählt
        </span>
      </div>

      <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Lade Schüler…
          </div>
        ) : students.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Keine Treffer.</p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {students.map((s) => {
              const id = String(s.ID);
              const checked = selectedIds.includes(id);
              const label = `${s.Vorname ?? ''} ${s.Name ?? ''}`.trim() || id;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => toggleSelection(id)}
                    className={`
                      flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors
                      ${checked ? 'bg-primary-50 dark:bg-primary-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}
                    `}
                  >
                    {checked ? (
                      <CheckSquare className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                    ) : (
                      <Square className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                    )}
                    <span className="min-w-0 flex-1 font-medium text-gray-900 dark:text-gray-100">{label}</span>
                    {s.Klasse ? (
                      <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{s.Klasse}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
